import { useState, useCallback, useRef } from 'react';
import { analyseUrl, recalculateScores } from '../api';
import { useToast } from '../ToastContext';

/**
 * Relance SEO + PageSpeed uniquement sur une liste d’URLs (ex. pages de référence).
 * Chaque URL est ré-analysée via analyse-url : seule cette URL est remplacée en base.
 */
export default function useReferencePagesAudit(projectSlug) {
  const { showToast } = useToast();
  const [isAuditing, setIsAuditing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentUrl, setCurrentUrl] = useState('');
  const cancelledRef = useRef(false);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
  }, []);

  const auditPages = useCallback(async (urls, { onPageDone, onComplete } = {}) => {
    const list = (urls || []).filter(
      (u) => typeof u === 'string' && u.trim() && /^https?:\/\//i.test(u.trim()),
    );
    if (!list.length) {
      showToast('Aucune page à auditer.', 'error');
      return { ok: false, audited: 0, failures: 0 };
    }
    if (!projectSlug) {
      showToast('Projet introuvable.', 'error');
      return { ok: false, audited: 0, failures: 0 };
    }

    cancelledRef.current = false;
    setIsAuditing(true);
    setProgress(0);
    setCurrentUrl('');
    let failures = 0;

    try {
      for (let i = 0; i < list.length; i++) {
        if (cancelledRef.current) break;
        const url = list[i].trim();
        setCurrentUrl(url);
        setProgress(Math.round((i / list.length) * 100));

        try {
          const result = await analyseUrl(projectSlug, url, false);
          onPageDone?.(url, result);
        } catch (err) {
          failures += 1;
          const msg = err.response?.data?.detail || err.message || 'Erreur';
          onPageDone?.(url, { url, error: true, errorMsg: msg });
        }

        setProgress(Math.round(((i + 1) / list.length) * 100));
        if (i < list.length - 1 && !cancelledRef.current) {
          await new Promise((r) => setTimeout(r, 500));
        }
      }

      if (!cancelledRef.current) {
        try {
          await recalculateScores(projectSlug);
        } catch (e) {
          console.warn('recalculateScores:', e);
        }
      }

      await onComplete?.();

      if (cancelledRef.current) {
        showToast('Audit interrompu.', 'info');
      } else if (failures > 0) {
        showToast(
          `${list.length - failures}/${list.length} page(s) mises à jour. Les autres URLs du projet sont inchangées.`,
          'warning',
        );
      } else {
        showToast(
          `${list.length} page(s) de référence ré-auditées. Le reste du tableau URL & SEO est conservé.`,
          'success',
        );
      }

      return {
        ok: failures === 0 && !cancelledRef.current,
        audited: list.length - failures,
        failures,
        cancelled: cancelledRef.current,
      };
    } finally {
      setIsAuditing(false);
      setCurrentUrl('');
      if (!cancelledRef.current) setProgress(100);
    }
  }, [projectSlug, showToast]);

  return {
    isAuditing,
    progress,
    currentUrl,
    auditPages,
    cancel,
  };
}
